import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { WORK_TYPE_COLOR_MAP } from '../constants';
import { extractRoleDetails, formatJobStartDate } from '../functions';
import type { IJobCardProps } from '../interfaces';

export const JobCard = ({ jobPostData, viewMode = 'grid' }: IJobCardProps) => {
  const { t } = useTranslation(NS.jobPortal);
  const history = useHistory();
  const roleDetails = extractRoleDetails(jobPostData.htmlTemplate);
  const startDate = formatJobStartDate(jobPostData.startDate);

  const handleApply = (): void => {
    history.push(`/jobs/${jobPostData.id}`);
  };

  const workTypeChip = (
    <Chip
      icon={<BusinessIcon fontSize="small" />}
      label={jobPostData.workType || 'Work type'}
      size="small"
      variant="outlined"
      color={WORK_TYPE_COLOR_MAP[jobPostData.workType] ?? 'default'}
      sx={{ fontWeight: 700 }}
    />
  );

  const fullTimeChip = (
    <Chip
      icon={<AccessTimeIcon fontSize="small" />}
      label={t(JOB_PORTAL_KEYS.card.fullTime)}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 700 }}
    />
  );

  if (viewMode === 'list') {
    return (
      <Card sx={{ p: 0 }}>
        <CardContent
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1fr) auto' },
            alignItems: { xl: 'center' },
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
            <Avatar variant="rounded" sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 40, height: 40 }}>
              <BusinessCenterIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h3" noWrap>
                  {jobPostData.jobTitle}
                </Typography>
                <Chip label="Open role" size="small" sx={{ bgcolor: 'grey.100', fontWeight: 700 }} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {jobPostData.department || 'General'}
                </Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {jobPostData.location || t(JOB_PORTAL_KEYS.card.remote)}
                </Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <CalendarMonthIcon fontSize="small" />
                  {startDate}
                </Typography>
              </Stack>
              {roleDetails && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {roleDetails}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: { xl: 'flex-end' } }}>
            {workTypeChip}
            {fullTimeChip}
            <Button variant="contained" onClick={handleApply} sx={{ minWidth: 128 }}>
              {t(JOB_PORTAL_KEYS.card.applyNow)}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
          <Avatar variant="rounded" sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 40, height: 40 }}>
            <BusinessCenterIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {jobPostData.department || 'General'}
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {jobPostData.jobTitle}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.100', px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              {t(JOB_PORTAL_KEYS.card.location)}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
              {jobPostData.location || t(JOB_PORTAL_KEYS.card.remote)}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <CalendarMonthIcon fontSize="small" />
            Posted {startDate}
          </Typography>
        </Stack>

        {roleDetails && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {roleDetails}
          </Typography>
        )}

        <Box sx={{ mt: 'auto' }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            {workTypeChip}
            {fullTimeChip}
          </Stack>
          <Button variant="contained" fullWidth onClick={handleApply}>
            {t(JOB_PORTAL_KEYS.card.applyNow)}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
